import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { PermissionService } from 'src/app/services/permission.service';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';

interface ShortcutEntry {
  keys: string[];
  labelKey: string;
  /** Only shown on this platform — omit for a shortcut that's the same everywhere. Used for
   *  entries whose real key differs by OS (e.g. Cmd on Mac vs Ctrl on Windows for the same
   *  physical action), so the dialog never shows a key combo that doesn't work on the viewer's
   *  own keyboard. */
  platform?: 'mac' | 'other';
}

interface ShortcutGroup {
  titleKey: string;
  entries: ShortcutEntry[];
}

/** True on macOS/iOS — used only to choose which entries to SHOW in this dialog. The underlying
 *  key handlers themselves already accept the right physical key per platform on their own (see
 *  e.g. side-bar.component.ts's handleKeyboardEvent, which checks metaKey OR ctrlKey) — this is
 *  purely a display concern, not something the handlers depend on. */
function isMacPlatform(): boolean {
  const platform = (navigator as any).userAgentData?.platform ?? navigator.platform ?? navigator.userAgent ?? '';
  return /mac/i.test(platform);
}

/** Every real keyboard shortcut in the app today, grouped by where it applies — compiled by
 *  grepping every @HostListener(...:keydown...) across tms-ng (2026-08-20, refreshed 2026-08-25
 *  after a full re-audit turned up several real gaps — see that audit's notes for what changed).
 *  Keep this in sync when adding/removing a shortcut elsewhere in the app; there is no single
 *  source of truth this reads from, since the shortcuts themselves live on their own components
 *  (Global Search, Quick Search, the sidebar, tally grids, CRUD forms, AI Chat, etc.). */
const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    titleKey: 'shortcuts.group.anywhere',
    entries: [
      { keys: ['/'], labelKey: 'shortcuts.openSearch' },
      { keys: ['Ctrl', 'Alt', 'C'], labelKey: 'shortcuts.calculator' },
      { keys: ['?'], labelKey: 'shortcuts.showShortcuts' },
      { keys: ['Alt', 'M'], labelKey: 'shortcuts.expandMenu' },
      { keys: ['⌘', 'Shift', 'M'], labelKey: 'shortcuts.expandMenu', platform: 'mac' },
      { keys: ['Ctrl', 'Shift', 'M'], labelKey: 'shortcuts.expandMenu', platform: 'other' },
    ],
  },
  {
    titleKey: 'shortcuts.group.search',
    entries: [
      { keys: ['↑', '↓'], labelKey: 'shortcuts.moveSelection' },
      { keys: ['Enter'], labelKey: 'shortcuts.openSelected' },
      { keys: ['Esc'], labelKey: 'shortcuts.closeSearch' },
    ],
  },
  {
    titleKey: 'shortcuts.group.tally',
    entries: [
      { keys: ['Ctrl', 'S'], labelKey: 'shortcuts.saveTally' },
      { keys: ['Enter'], labelKey: 'shortcuts.tallyEnter' },
      { keys: ['Shift', 'Enter'], labelKey: 'shortcuts.tallyAddRowBelow' },
      { keys: ['Shift', 'Enter'], labelKey: 'shortcuts.tallyStockPicker' },
      { keys: ['Ctrl', 'Enter'], labelKey: 'shortcuts.tallyForceNext' },
      { keys: ['Ctrl', '←', '→', '↑', '↓'], labelKey: 'shortcuts.tallyMoveCells' },
    ],
  },
  {
    titleKey: 'shortcuts.group.forms',
    entries: [
      { keys: ['⌘', 'Shift', 'S'], labelKey: 'shortcuts.saveForm' },
      { keys: ['⌘', 'Shift', 'D'], labelKey: 'shortcuts.deleteForm' },
    ],
  },
  {
    titleKey: 'shortcuts.group.photos',
    entries: [
      { keys: ['←', '→'], labelKey: 'shortcuts.prevNextPhoto' },
      { keys: ['Esc'], labelKey: 'shortcuts.closePhoto' },
    ],
  },
  {
    titleKey: 'shortcuts.group.calculator',
    entries: [
      { keys: ['0', '–', '9'], labelKey: 'shortcuts.calcDigits' },
      { keys: ['+', '−', '×', '÷'], labelKey: 'shortcuts.calcOperators' },
      { keys: ['%'], labelKey: 'shortcuts.calcPercent' },
      { keys: ['Enter'], labelKey: 'shortcuts.calcEquals' },
      { keys: ['Backspace'], labelKey: 'shortcuts.calcBackspace' },
      { keys: ['Esc'], labelKey: 'shortcuts.calcClearOrClose' },
    ],
  },
  {
    titleKey: 'shortcuts.group.aiChat',
    entries: [
      { keys: ['Enter'], labelKey: 'shortcuts.aiChatSend' },
      { keys: ['Shift', 'Enter'], labelKey: 'shortcuts.aiChatNewLine' },
    ],
  },
];

/** Global "?" cheat-sheet, the same convention Gmail/Trello/Slack/Notion/Linear/GitHub all use —
 *  zero permanent header chrome, purely a keyboard-discoverable + one link-from-Global-Search's-
 *  help-panel affordance (see GlobalSearchComponent's help panel). */
@Component({
  selector: 'app-keyboard-shortcuts',
  templateUrl: './keyboard-shortcuts.component.html',
  styleUrls: ['./keyboard-shortcuts.component.css'],
  standalone: true,
  imports: [SHARED_MODULES],
})
export class KeyboardShortcutsComponent implements OnInit, OnDestroy {
  visible = false;
  /** SHORTCUT_GROUPS with any platform-specific entry (e.g. the Mac vs Windows expand-menu key)
   *  filtered down to just the one that actually works on this viewer's own keyboard. */
  readonly groups: ShortcutGroup[] = this.filterByPlatform(SHORTCUT_GROUPS);

  private openSub?: Subscription;

  constructor(
    public permissionService: PermissionService,
    private keyboardShortcutsService: KeyboardShortcutsService,
  ) {}

  ngOnInit(): void {
    // Lets Global Search's help panel (a sibling component, not a parent/child) open this dialog
    // too, without a direct reference to it.
    this.openSub = this.keyboardShortcutsService.open$.subscribe(() => this.open());
  }

  private filterByPlatform(groups: ShortcutGroup[]): ShortcutGroup[] {
    const mac = isMacPlatform();
    return groups.map(group => ({
      ...group,
      entries: group.entries.filter(entry => !entry.platform || (entry.platform === 'mac') === mac),
    }));
  }

  ngOnDestroy(): void {
    this.openSub?.unsubscribe();
  }

  /** Matches UnifiedSearchComponent's own canUse — either search permission is enough, since
   *  this panel now documents both engines' shortcuts (merged into one dialog, 2026-08-20). */
  get canUse(): boolean {
    return this.permissionService.hasEffective('GLOBAL_SEARCH_ACCESS')
      || this.permissionService.hasEffective('QUICK_SEARCH_ACCESS');
  }

  open(): void {
    this.visible = true;
  }

  close(): void {
    this.visible = false;
  }

  /** "?" — ignored while typing anywhere (a literal "?" in a text field must never be eaten),
   *  while already open, without GLOBAL_SEARCH_ACCESS (this dialog documents Global Search's own
   *  shortcuts, so it's gated the same way), or alongside any modifier (Shift+/ producing "?" on
   *  a US layout is exactly what should trigger this — checking event.key === '?' already handles
   *  that correctly without an explicit shiftKey check). */
  @HostListener('document:keydown', ['$event'])
  handleShortcut(event: KeyboardEvent): void {
    if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey) return;
    if (this.visible || !this.canUse) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    this.open();
  }
}
